/**
 * Short synthesised tones for voice connect/disconnect feedback. Generated
 * via Web Audio API so we don't need to ship audio files. Replace with an
 * <audio> element + asset if/when we want a custom branded sound.
 */

function playTone(opts: {
  startHz: number;
  endHz: number;
  durationMs: number;
  volume?: number;
}) {
  if (typeof window === 'undefined') return;
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.connect(gain);
    gain.connect(ctx.destination);

    const { startHz, endHz, durationMs, volume = 0.12 } = opts;
    const seconds = durationMs / 1000;
    const now = ctx.currentTime;

    osc.frequency.setValueAtTime(startHz, now);
    osc.frequency.exponentialRampToValueAtTime(endHz, now + seconds);

    // Quick attack, gentle release — avoids the click of a hard cutoff.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds);

    osc.start(now);
    osc.stop(now + seconds + 0.05);
    osc.onended = () => ctx.close().catch(() => {});
  } catch {
    // Browsers may block AudioContext until user gesture; voice itself
    // requires a user gesture, so by the time we get here the ctx should
    // be allowed. Swallow any failure silently.
  }
}

/** Two-note rising chime — played when the voice session connects. */
export function playConnectSound() {
  playTone({ startHz: 440, endHz: 660, durationMs: 220 });
}

/** Two-note falling chime — played when the voice session ends. */
export function playDisconnectSound() {
  playTone({ startHz: 660, endHz: 330, durationMs: 220 });
}

/**
 * A UK ringing tone, looped, for the wait between hearing the wake word and the
 * session actually connecting.
 *
 * 🚨 This is the answer to a real measurement, not decoration: ~6 seconds from
 * "Alright Mucka" to connected on a phone. Most of that is the ElevenLabs SDK
 * downloading and WebRTC negotiating, and some of it will always be there. The
 * mic button spends the same time behind a spinner; the wake word had the user
 * stood in silence having spoken out loud, which is the version that feels
 * broken.
 *
 * Ringing rather than another chime, because it says the one thing a chime
 * cannot: *keep waiting, something is coming*. Everybody already knows what a
 * ring means and that answering follows it, so the connect sweep stops being a
 * second unexplained noise and becomes the call being picked up.
 *
 * The British double ring: two 400ms bursts of 400Hz+450Hz split by 200ms, then
 * two seconds of silence. Two oscillators genuinely beating against each other,
 * because that warble is the whole character of the sound.
 */
export function startRinging(ctx: AudioContext): () => void {
  // 🚨 The context is passed IN, and that is the fix for a real bug. This used
  // to create its own, and on the wake-word path that happens with no user
  // gesture, moments after we have just closed the capture context — on iOS it
  // comes back SUSPENDED and schedules its bursts against a clock that is not
  // running, so it plays silence. The mic button rang perfectly because its
  // context was created inside the tap. `ringing.ts` now owns one long-lived
  // context, primed in a gesture and kept running.

  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const voices = new Set<{ osc: OscillatorNode; gain: GainNode; at: number }>();

  const burst = (at: number, seconds: number) => {
    for (const hz of [400, 450]) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const voice = { osc, gain, at };
      voices.add(voice);
      osc.onended = () => {
        voices.delete(voice);
        osc.disconnect(); gain.disconnect();
      };
      osc.type = 'sine';
      osc.frequency.value = hz;
      // Ramped, never switched: a square edge on a repeating tone clicks every
      // single time and turns a ring into a fault.
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.09, at + 0.02);
      gain.gain.setValueAtTime(0.09, at + seconds - 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + seconds);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + seconds + 0.02);
    }
  };

  const ring = () => {
    if (stopped) return;
    const now = ctx.currentTime;
    burst(now, 0.4);
    burst(now + 0.6, 0.4);
    // 3s cycle: ring, ring, wait. Scheduled off a timer rather than a single
    // long chain so stopping is immediate — nothing is queued beyond the pair
    // currently sounding.
    timer = setTimeout(ring, 3000);
  };
  ring();

  return () => {
    if (stopped) return;
    stopped = true;
    if (timer) clearTimeout(timer);
    // Clearing the timer alone leaves up to a second of already scheduled
    // audio, including the second burst, underneath the connected reply.
    // Fade sounding nodes and cancel future ones; keep the shared context.
    const now = ctx.currentTime;
    for (const { osc, gain, at } of voices) {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(at > now ? 0.0001 : Math.max(0.0001, gain.gain.value), now);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.02);
      osc.stop(now + 0.03);
    }
  };
}

/**
 * The three sounds a call makes when the line drops (TKT-1462), for a page: a
 * gentle pulse repeated while it is trying, the connect chime when it is back,
 * and a falling swoop when it gives up. The native runtimes play these for a
 * call (`VoiceProofTones` in Swift and Kotlin); dictation plays them here, so
 * a dropped line sounds the same whichever one dropped
 * (`a-call-holds-on-through-a-signal-dip.test.ts` compares all three lists).
 */
export type LineCue = 'reconnecting' | 'reconnected' | 'dropped';
export interface LineSegment { from: number; to: number; seconds: number; volume: number; attack: number; release: number; overtones: boolean; exponential: boolean }
export const LINE_CUE_SEGMENTS: Record<LineCue, LineSegment[]> = {
  reconnecting: [{ from: 523, to: 523, seconds: 0.45, volume: 0.22, attack: 0.18, release: 0.25, overtones: true, exponential: false }],
  reconnected: [{ from: 440, to: 660, seconds: 0.22, volume: 0.12, attack: 0.01, release: 0, overtones: false, exponential: true }],
  dropped: [{ from: 660, to: 330, seconds: 0.4, volume: 0.25, attack: 0.02, release: 0.12, overtones: false, exponential: false }],
};
/** How often the reconnecting pulse repeats. */
export const LINE_CUE_REPEAT_MS = 2500;

/** Play one cue on a running context. A tone is a courtesy: nothing here may throw. */
export function playLineCue(ctx: AudioContext, cue: LineCue): void {
  try {
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    let at = ctx.currentTime;
    for (const s of LINE_CUE_SEGMENTS[cue]) {
      const out = ctx.createGain();
      out.connect(ctx.destination);
      const end = at + s.seconds;
      if (s.exponential) {
        out.gain.setValueAtTime(0.0001, at);
        out.gain.exponentialRampToValueAtTime(s.volume, at + s.attack);
        out.gain.exponentialRampToValueAtTime(0.0001, end);
      } else {
        out.gain.setValueAtTime(0, at);
        out.gain.linearRampToValueAtTime(s.volume, at + s.attack);
        out.gain.setValueAtTime(s.volume, Math.max(at + s.attack, end - s.release));
        out.gain.linearRampToValueAtTime(0, end);
      }
      // The overtones are what a phone speaker actually plays of a note this low.
      const partials = s.overtones ? [[1, 1 / 1.24], [2, 0.18 / 1.24], [3, 0.06 / 1.24]] : [[1, 1]];
      for (const [n, w] of partials) {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(s.from * n, at);
        if (s.to !== s.from) {
          if (s.exponential) osc.frequency.exponentialRampToValueAtTime(s.to * n, end);
          else osc.frequency.linearRampToValueAtTime(s.to * n, end);
        }
        g.gain.value = w;
        osc.connect(g).connect(out);
        osc.start(at);
        osc.stop(end + 0.02);
        osc.onended = () => { osc.disconnect(); g.disconnect(); };
      }
      at = end;
    }
  } catch {
    // Web Audio unavailable or refused: the screen still says it.
  }
}
